// Where the session key and the nonces live: IndexedDB, each record sealed with AES-GCM under a
// key the browser made non-extractable, so the database alone never yields a session secret.
// Records past their time are dropped on read. Without IndexedDB (a private window in some
// browsers) they live in memory, for this tab only.

import type { SdkStore } from "./wasm/papertrade";

const DB = "papertrade";
const VERSION = 1;
const KEYS = "keys";
const KEPT = "kept";
const SEAL = "seal";

type Sealed = { iv: Uint8Array; data: ArrayBuffer; expiresAt: number | null };

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexeddb request failed"));
  });
}

let opened: Promise<IDBDatabase> | null = null;

function database(): Promise<IDBDatabase> {
  opened ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS);
      if (!db.objectStoreNames.contains(KEPT)) db.createObjectStore(KEPT);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexeddb unavailable"));
    req.onblocked = () => reject(new Error("indexeddb blocked"));
  });
  return opened;
}

async function store(name: string, mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return (await database()).transaction(name, mode).objectStore(name);
}

let sealing: Promise<CryptoKey> | null = null;

function sealKey(): Promise<CryptoKey> {
  sealing ??= (async () => {
    const found = await request((await store(KEYS, "readonly")).get(SEAL));
    if (found instanceof CryptoKey) return found;
    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await request((await store(KEYS, "readwrite")).put(key, SEAL));
    return key;
  })();
  return sealing;
}

const name = (wallet: string, slot: string) => `${wallet.toLowerCase()}:${slot}`;

const memory = new Map<string, { bytes: Uint8Array; expiresAt: number | null }>();

async function durable(): Promise<boolean> {
  try {
    await database();
    await sealKey();
    return true;
  } catch {
    return false;
  }
}

let usable: Promise<boolean> | null = null;
const isDurable = () => (usable ??= durable());

export const vault: SdkStore = {
  async get(wallet, slot) {
    const key = name(wallet, slot);
    if (!(await isDurable())) {
      const held = memory.get(key);
      if (!held || (held.expiresAt !== null && held.expiresAt <= Date.now())) return null;
      return held.bytes;
    }
    const sealed = (await request((await store(KEPT, "readonly")).get(key))) as Sealed | undefined;
    if (!sealed) return null;
    if (sealed.expiresAt !== null && sealed.expiresAt <= Date.now()) {
      await request((await store(KEPT, "readwrite")).delete(key));
      return null;
    }
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: sealed.iv as Uint8Array<ArrayBuffer> },
      await sealKey(),
      sealed.data,
    );
    return new Uint8Array(plain);
  },

  async put(wallet, slot, bytes, ttlMs) {
    const key = name(wallet, slot);
    const expiresAt = ttlMs === undefined ? null : Date.now() + ttlMs;
    const empty = bytes.length === 0 || ttlMs === 0;
    if (!(await isDurable())) {
      if (empty) memory.delete(key);
      else memory.set(key, { bytes: bytes.slice(), expiresAt });
      return;
    }
    if (empty) {
      await request((await store(KEPT, "readwrite")).delete(key));
      return;
    }
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await sealKey(), bytes as Uint8Array<ArrayBuffer>);
    const sealed: Sealed = { iv, data, expiresAt };
    await request((await store(KEPT, "readwrite")).put(sealed, key));
  },
};

/** Whether keys survive a reload here. */
export const vaultIsDurable = isDurable;
